import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  makeStyles,
  shorthands,
  tokens,
  TabList,
  Tab,
  Spinner,
  Badge,
} from '@fluentui/react-components';
import { apiFetch } from '../lib/apiFetch';

interface SearchResult {
  id: string;
  type: 'customer' | 'order' | 'invoice' | 'quote' | 'contract' | 'product' | 'inventory';
  title: string;
  subtitle?: string;
  details?: string;
}

type TabValue = 'all' | 'customers' | 'orders' | 'quotes' | 'invoices' | 'contracts' | 'products' | 'inventory';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#f5f5f5',
  },
  header: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.padding('24px'),
    boxShadow: tokens.shadow8,
  },
  searchQuery: {
    fontSize: '28px',
    fontWeight: 600,
    marginBottom: '8px',
    color: tokens.colorNeutralForeground1,
  },
  resultCount: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
  },
  tabsContainer: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.padding('0', '24px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  content: {
    ...shorthands.padding('24px'),
    flex: 1,
    overflowY: 'auto',
  },
  resultsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  resultCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow8,
    ...shorthands.padding('16px', '20px'),
    cursor: 'pointer',
    ...shorthands.border('1px', 'solid', 'transparent'),
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    '&:hover': {
      ...shorthands.borderColor(tokens.colorBrandStroke1),
      boxShadow: tokens.shadow8,
    },
  },
  resultContent: {
    flex: 1,
    minWidth: 0,
  },
  resultTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    marginBottom: '6px',
  },
  resultSubtitle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
    marginBottom: '4px',
  },
  resultDetails: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
  },
  typeBadge: {
    flexShrink: 0,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px',
  },
  emptyState: {
    textAlign: 'center',
    ...shorthands.padding('80px', '24px'),
    color: tokens.colorNeutralForeground3,
  },
  emptyStateTitle: {
    fontSize: '20px',
    fontWeight: 600,
    marginBottom: '8px',
  },
  emptyStateText: {
    fontSize: '14px',
  },
});

const SearchResults: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [selectedTab, setSelectedTab] = useState<TabValue>('all');
  const [loading, setLoading] = useState(true);
  const [allResults, setAllResults] = useState<SearchResult[]>([]);

  useEffect(() => {
    const fetchResults = async () => {
      if (!query || query.length < 2) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await apiFetch(`/global-search?q=${encodeURIComponent(query)}&limit=50`);

        if (!response.ok) {
          throw new Error('Search failed');
        }

        const data = await response.json();
        setAllResults(data.results || []);
      } catch (err) {
        console.error('Search error:', err);
        setAllResults([]);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [query]);

  const getFilteredResults = () => {
    if (selectedTab === 'all') {
      return allResults;
    }

    const typeMap: Record<TabValue, string> = {
      'all': '',
      'customers': 'customer',
      'orders': 'order',
      'products': 'product',
      'inventory': 'inventory',
      'quotes': 'quote',
      'invoices': 'invoice',
      'contracts': 'contract',
    };

    return allResults.filter(result => result.type === typeMap[selectedTab]);
  };

  const getCountForTab = (tab: TabValue): number => {
    if (tab === 'all') return allResults.length;

    const typeMap: Record<TabValue, string> = {
      'all': '',
      'customers': 'customer',
      'orders': 'order',
      'products': 'product',
      'inventory': 'inventory',
      'quotes': 'quote',
      'invoices': 'invoice',
      'contracts': 'contract',
    };

    return allResults.filter(result => result.type === typeMap[tab]).length;
  };

  const handleResultClick = (result: SearchResult) => {
    switch (result.type) {
      case 'customer':
        navigate(`/customers/${result.id}`);
        break;
      case 'order':
        navigate(`/orders/${result.id}`);
        break;
      case 'product':
        navigate(`/inventory?product=${result.id}`);
        break;
      case 'inventory':
        navigate(`/inventory?item=${result.id}`);
        break;
      case 'invoice':
        navigate(`/invoices/${result.id}`);
        break;
      case 'quote':
        navigate(`/quotes/${result.id}`);
        break;
      case 'contract':
        navigate(`/contracts/${result.id}`);
        break;
    }
  };

  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'customer':
        return { color: 'brand' as const, label: 'Customer' };
      case 'order':
        return { color: 'success' as const, label: 'Order' };
      case 'product':
        return { color: 'informative' as const, label: 'Product' };
      case 'inventory':
        return { color: 'severe' as const, label: 'Inventory' };
      case 'invoice':
        return { color: 'warning' as const, label: 'Invoice' };
      case 'quote':
        return { color: 'informative' as const, label: 'Quote' };
      case 'contract':
        return { color: 'important' as const, label: 'Contract' };
      default:
        return { color: 'subtle' as const, label: type };
    }
  };

  const filteredResults = getFilteredResults();

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.searchQuery}>Search results for "{query}"</div>
        <div className={styles.resultCount}>
          {allResults.length} {allResults.length === 1 ? 'result' : 'results'} found
        </div>
      </div>

      <div className={styles.tabsContainer}>
        <TabList
          selectedValue={selectedTab}
          onTabSelect={(_, data) => setSelectedTab(data.value as TabValue)}
        >
          <Tab value="all">All ({getCountForTab('all')})</Tab>
          <Tab value="customers">Customers ({getCountForTab('customers')})</Tab>
          <Tab value="orders">Orders ({getCountForTab('orders')})</Tab>
          <Tab value="products">Products ({getCountForTab('products')})</Tab>
          <Tab value="inventory">Inventory ({getCountForTab('inventory')})</Tab>
          <Tab value="quotes">Quotes ({getCountForTab('quotes')})</Tab>
          <Tab value="invoices">Invoices ({getCountForTab('invoices')})</Tab>
          <Tab value="contracts">Contracts ({getCountForTab('contracts')})</Tab>
        </TabList>
      </div>

      <div className={styles.content}>
        {loading && (
          <div className={styles.loadingContainer}>
            <Spinner size="large" label="Searching..." />
          </div>
        )}

        {!loading && filteredResults.length === 0 && (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateTitle}>No results found</div>
            <div className={styles.emptyStateText}>
              Try adjusting your search query or search in a different category
            </div>
          </div>
        )}

        {!loading && filteredResults.length > 0 && (
          <div className={styles.resultsList}>
            {filteredResults.map((result) => {
              const badge = getTypeBadgeColor(result.type);
              return (
                <div
                  key={`${result.type}-${result.id}`}
                  className={styles.resultCard}
                  onClick={() => handleResultClick(result)}
                >
                  <Badge appearance="filled" color={badge.color} className={styles.typeBadge}>
                    {badge.label}
                  </Badge>
                  <div className={styles.resultContent}>
                    <div className={styles.resultTitle}>{result.title}</div>
                    {result.subtitle && (
                      <div className={styles.resultSubtitle}>{result.subtitle}</div>
                    )}
                    {result.details && (
                      <div className={styles.resultDetails}>{result.details}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResults;
